import React from 'react';
import { View, Text, StyleSheet } from 'react-native';
import i18n from 'i18n-js';
import DashedLine from 'react-native-dashed-line';

import { scale } from '@components/frame';
import { font } from '@styles/fonts';
import { formatOEEPercent } from '@helpers';

import TouchableElement from '@components/common/touchableElement';
import { connect } from 'react-redux';
import { TEST_IDS } from '@v2/pages/common/constants/base.constants';
import { useManageUtilizationBar } from '@components/machines/components/Utilization/components/chart/hooks/useManageUtilizationBar';

const Bar = ({
  item,
  padding,
  chartHeight,
  chartWidth,
  getMachineStatsOeeTeepPerPeriod,
  id,
  barBlockWidth,
  barWidth,
  C,
  saveChartData,
  selectedDate,
  setChartData,
  chartData,
  selectedBarIndex,
  setSelectedBarIndex,
}) => {
  const { data = {} } = item;

  const { legend, legendRules, getBarPosition, isOeeLabelVisible } = useManageUtilizationBar({
    item,
    id,
    barBlockWidth,
    selectedDate,
    saveChartData,
    getMachineStatsOeeTeepPerPeriod,
  });

  return (
    <View>
      <View
        testID={TEST_IDS.utilizationBar.chartContainer}
        style={{
          width: chartWidth,
          paddingHorizontal: padding,
          height: chartHeight,
          justifyContent: 'center',
          alignContent: 'center',
          alignItems: 'center',
        }}
        key={`chart-${item.date}`}
      >
        {data?.data?.map((barItem, index) => {
          const isSelected = selectedBarIndex === index;
          return (
            <TouchableElement
              key={`bar-${index}`}
              hitSlop={{ top: 0, bottom: 0, left: 0, right: 0 }}
              onPress={() => {
                setSelectedBarIndex(isSelected ? null : index);
              }}
            >
              {({ boxStyle }) => {
                return (
                  <View
                    style={[
                      boxStyle,
                      {
                        height: '100%',
                        flexDirection: 'row',
                        alignItems: 'flex-end',
                        width: barBlockWidth,
                        position: 'absolute',
                        zIndex: 1000,
                        left: getBarPosition(barItem, index),
                        opacity: selectedBarIndex !== null && !isSelected ? 0.5 : 1,
                      },
                    ]}
                  >
                    <View
                      style={[
                        {
                          width: barBlockWidth,
                          height: `${(barItem.oee || 0) * 100}%`,
                          minHeight: barItem.oee ? 1 : 0,
                          alignItems: 'center',
                        },
                      ]}
                    >
                      {isOeeLabelVisible(barItem, chartHeight) && (
                        <Text
                          testID={TEST_IDS.utilizationBar.oeePercentageLabel}
                          numberOfLines={1}
                          style={[
                            font.Footnote,
                            {
                              color: C('OEE/Light/OEE Chart'),
                              position: 'absolute',
                              bottom: '100%',
                              marginBottom: scale(2),
                              width: barBlockWidth,
                              textAlign: 'center',
                            },
                          ]}
                        >
                          {formatOEEPercent(barItem.oee)}
                        </Text>
                      )}
                      {data.loaded ? (
                        <View
                          style={[
                            boxStyle,
                            {
                              width: barWidth,
                              height: '100%',
                              justifyContent: 'flex-end',
                              alignItems: 'center',
                              borderTopLeftRadius: scale(2),
                              borderTopRightRadius: scale(2),
                              overflow: 'hidden',
                              backgroundColor: C('OEE/Light/OEE Chart'),
                            },
                          ]}
                        >
                          {!!barItem.teep && barItem.oee > 0 && (
                            <View
                              style={{
                                width: '60%',
                                height: `${(barItem.teep / barItem.oee) * 100}%`,
                                backgroundColor: C('OEE/Light/TEEP'),
                                borderTopLeftRadius: scale(1),
                                borderTopRightRadius: scale(1),
                                position: 'absolute',
                                bottom: 0,
                                alignSelf: 'center',
                              }}
                            />
                          )}
                        </View>
                      ) : (
                        <View
                          nativeID="shimmer"
                          style={{
                            width: barWidth,
                            height: '100%',
                            justifyContent: 'flex-end',
                            alignItems: 'center',
                            borderTopLeftRadius: scale(2),
                            borderTopRightRadius: scale(2),
                            overflow: 'hidden',
                            backgroundColor: C('Gray/Light/3'),
                          }}
                        />
                      )}
                    </View>
                  </View>
                );
              }}
            </TouchableElement>
          );
        })}
      </View>
      {legend.map((legendItem, index) => (
        <View
          key={`legend-${index}-${item.date}`}
          pointerEvents="none"
          style={{
            position: 'absolute',
            top: 0,
            width: barBlockWidth,
            left: index * barBlockWidth,
            height: scale(136),
          }}
        >
          {legendRules.includes(legendItem) ? (
            <>
              <View
                style={{
                  position: 'absolute',
                  width: StyleSheet.hairlineWidth,
                  height: '100%',
                  left: -StyleSheet.hairlineWidth / 2,
                }}
              >
                <DashedLine
                  axis="vertical"
                  style={{ height: scale(136) }}
                  dashThickness={StyleSheet.hairlineWidth}
                  dashLength={scale(2)}
                  dashGap={scale(2)}
                  dashColor={C('Label/Light/Quaternary')}
                />
              </View>
              <Text
                numberOfLines={1}
                style={[
                  font.Footnote,
                  {
                    color: C('Label/Light/Quaternary'),
                    position: 'absolute',
                    bottom: scale(-3),
                    left: (barBlockWidth - barWidth) / 2,
                    minWidth: scale(20),
                  },
                ]}
              >
                {i18n.t(String(legendItem))}
              </Text>
            </>
          ) : null}
          {/* {index === legend.length - 1 && (
            <View
              style={{
                position: 'absolute',
                width: StyleSheet.hairlineWidth,
                height: '100%',
                right: 0,
              }}
            >
              <DashedLine
                axis="vertical"
                style={{ height: scale(136) }}
                dashThickness={StyleSheet.hairlineWidth}
                dashLength={scale(2)}
                dashGap={scale(2)}
                dashColor={C('Label/Light/Quaternary')}
              />
            </View>
          )} */}
        </View>
      ))}
      <View
        pointerEvents="none"
        style={{
          position: 'absolute',
          width: StyleSheet.hairlineWidth,
          height: '100%',
          right: 0,
        }}
      >
        <DashedLine
          axis="vertical"
          style={{ height: scale(136) }}
          dashThickness={StyleSheet.hairlineWidth}
          dashLength={scale(2)}
          dashGap={scale(2)}
          dashColor={C('Label/Light/Quaternary')}
        />
      </View>
    </View>
  );
};

export default connect(({ machinesV2: { chartData } }) => ({ chartData }), {})(Bar);
